import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import { Sidebar } from '../components/Sidebar';
import { Navbar } from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Download, Upload, Search, X, Building2, User, Calendar } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useEnquiries, useBranches, useTeamMembers, useCustomers } from '../hooks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const EnquiryList = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [branchFilter, setBranchFilter] = useState('all');
  const [createdByFilter, setCreatedByFilter] = useState('all');
  const [assignedToFilter, setAssignedToFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const fileInputRef = useRef(null);
  const customerInputRef = useRef(null);
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Use custom hooks for data fetching
  const { enquiries, loading, refetch: refetchEnquiries } = useEnquiries();
  const { branches } = useBranches();
  const { teamMembers, getFilteredByBranch } = useTeamMembers();
  const { customers, searchCustomers } = useCustomers();

  // Initialize filters from URL params
  useEffect(() => {
    const assignedTo = searchParams.get('assignedTo');
    const status = searchParams.get('status');
    
    if (assignedTo) {
      setAssignedToFilter(assignedTo);
    }
    if (status) {
      setStatusFilter(status);
    }
  }, [searchParams]);
  
  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    product_name: '',
    price_details: '',
    priority: 'medium',
    status: 'new',
    follow_up_date: '',
    remarks: ''
  });

  useEffect(() => {
    filterEnquiries();
  }, [searchQuery, branchFilter, createdByFilter, assignedToFilter, statusFilter, enquiries, customers]);

  const filterEnquiries = () => {
    let filtered = [...enquiries];
    
    // Apply branch filter
    if (branchFilter && branchFilter !== 'all') {
      filtered = filtered.filter(enquiry => enquiry.branch_id === branchFilter);
    }
    
    // Apply created by filter
    if (createdByFilter && createdByFilter !== 'all') {
      filtered = filtered.filter(enquiry => enquiry.created_by === createdByFilter);
    }
    
    // Apply assigned to filter
    if (assignedToFilter && assignedToFilter !== 'all') {
      if (assignedToFilter === 'unassigned') {
        filtered = filtered.filter(enquiry => !enquiry.assigned_to);
      } else {
        filtered = filtered.filter(enquiry => enquiry.assigned_to === assignedToFilter);
      }
    }
    
    // Apply status filter
    if (statusFilter && statusFilter !== 'all') {
      if (statusFilter === 'active') {
        // Active means not won or lost
        filtered = filtered.filter(enquiry => !['won', 'lost'].includes(enquiry.status));
      } else {
        filtered = filtered.filter(enquiry => enquiry.status === statusFilter);
      }
    }
    
    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(enquiry => {
        const customer = customers.find(c => c.id === enquiry.customer_id);
        return (
          enquiry.customer_name?.toLowerCase().includes(query) ||
          enquiry.product_name?.toLowerCase().includes(query) ||
          customer?.phone?.toLowerCase().includes(query) ||
          customer?.email?.toLowerCase().includes(query)
        );
      });
    }
    
    setFilteredEnquiries(filtered);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setBranchFilter('all');
    setCreatedByFilter('all');
    setAssignedToFilter('all');
    setStatusFilter('all');
    setSearchParams({});
  };

  const hasActiveFilters = searchQuery || branchFilter !== 'all' || createdByFilter !== 'all' || assignedToFilter !== 'all' || statusFilter !== 'all';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    if (submitting) return; // Prevent double submission
    
    setSubmitting(true);
    try {
      const submitData = {
        customer_id: formData.customer_id,
        product_name: formData.product_name,
        price_details: formData.price_details,
        priority: formData.priority,
        status: formData.status,
        follow_up_date: formData.follow_up_date,
        remarks: formData.remarks
      };
      await axios.post(`${API}/enquiries`, submitData);
      toast.success('Enquiry created successfully');
      setDialogOpen(false);
      setFormData({
        customer_id: '',
        customer_name: '',
        product_name: '',
        price_details: '',
        priority: 'medium',
        status: 'new',
        follow_up_date: '',
        remarks: ''
      });
      setCustomerSearch('');
      refetchEnquiries();
    } catch (error) {
      toast.error('Failed to create enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-900',
      contacted: 'bg-indigo-100 text-indigo-900',
      qualified: 'bg-purple-100 text-purple-900',
      proposal_sent: 'bg-white text-slate-900 border border-slate-300',
      won: 'bg-green-100 text-green-900',
      lost: 'bg-red-100 text-red-900',
    };
    return colors[status] || 'bg-slate-100 text-slate-900';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-slate-100 text-slate-900',
      medium: 'bg-white text-slate-900 border border-slate-300',
      high: 'bg-red-100 text-red-900',
    };
    return colors[priority] || 'bg-slate-100 text-slate-900';
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/enquiries/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `enquiries_export_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Enquiries exported successfully');
    } catch (error) {
      toast.error('Failed to export enquiries');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setImporting(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axios.post(`${API}/enquiries/import`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(response.data.message);
      if (response.data.errors?.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors`);
      }
      setImportDialogOpen(false);
      refetchEnquiries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import enquiries');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  // Use hook's search function for customers
  const filteredCustomers = searchCustomers(customerSearch);

  // Use hook's filter function for team members by branch
  const filteredTeamMembers = getFilteredByBranch(branchFilter);

  // Reset created by filter when branch changes and selected member is not in the new branch
  useEffect(() => {
    if (createdByFilter !== 'all' && branchFilter !== 'all') {
      const memberInBranch = teamMembers.find(m => m.id === createdByFilter && (m.branch_id === branchFilter || m.role === 'admin'));
      if (!memberInBranch) {
        setCreatedByFilter('all');
      }
    }
  }, [branchFilter, createdByFilter, teamMembers]);

  const handleCustomerSelect = (customer) => {
    setFormData({ ...formData, customer_id: customer.id, customer_name: customer.name });
    setCustomerSearch(customer.name);
    setShowCustomerDropdown(false);
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearch(value);
    setShowCustomerDropdown(value.length >= 3);
    if (value.length < 3) {
      setFormData({ ...formData, customer_id: '', customer_name: '' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar open={sidebarOpen} setOpen={setSidebarOpen} />
      <Navbar setSidebarOpen={setSidebarOpen} />
      
      <main className="md:ml-64 pt-16">
        <div className="p-6 md:p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="enquiries-title">Sales Enquiries</h1>
              <p className="text-slate-600">Track and manage sales pipeline</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleExport} data-testid="export-enquiries-button">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
              <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" data-testid="import-enquiries-button">
                    <Upload className="h-4 w-4 mr-2" />
                    Import
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Import Enquiries from Excel</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <p className="text-sm text-slate-600">
                      Upload an Excel file (.xlsx) with columns: Customer (name or email), Product Name, Price Details, Status, Priority, Follow-up Date, Remarks
                    </p>
                    <Input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleImport} disabled={importing} data-testid="import-enquiries-file" />
                    {importing && <p className="text-sm text-slate-600">Importing...</p>}
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) {
                  setCustomerSearch('');
                  setShowCustomerDropdown(false);
                }
              }}>
                <DialogTrigger asChild>
                  <Button data-testid="create-enquiry-button">
                    <Plus className="h-4 w-4 mr-2" />
                    New Enquiry
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Enquiry</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Customer Search Input */}
                    <div className="space-y-2">
                      <Label htmlFor="customer_search">Customer Name *</Label>
                      <p className="text-xs text-slate-500">Type at least 3 characters to search</p>
                      <div className="relative">
                        <Input
                          ref={customerInputRef}
                          id="customer_search"
                          value={customerSearch}
                          onChange={handleCustomerSearchChange}
                          onFocus={() => customerSearch.length >= 3 && setShowCustomerDropdown(true)}
                          placeholder="Type customer name or phone..."
                          autoComplete="off"
                          data-testid="enquiry-customer-search"
                        />
                        {showCustomerDropdown && filteredCustomers.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
                            {filteredCustomers.map((customer) => (
                              <div
                                key={customer.id}
                                className="px-4 py-2 hover:bg-slate-100 cursor-pointer border-b border-slate-100 last:border-b-0"
                                onClick={() => handleCustomerSelect(customer)}
                                data-testid={`customer-option-${customer.id}`}
                              >
                                <p className="font-medium text-slate-900">{customer.name}</p>
                                <p className="text-xs text-slate-500">{customer.phone} • {customer.email}</p>
                              </div>
                            ))}
                          </div>
                        )}
                        {showCustomerDropdown && customerSearch.length >= 3 && filteredCustomers.length === 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-4 text-center text-slate-500">
                            No customers found
                          </div>
                        )}
                      </div>
                      {formData.customer_id && (
                        <p className="text-xs text-green-600">✓ Customer selected: {formData.customer_name}</p>
                      )}
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="product_name">Product Name *</Label>
                      <Input
                        id="product_name"
                        value={formData.product_name}
                        onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                        required
                        data-testid="enquiry-product-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="price_details">Price Details (₹)</Label>
                      <Input
                        id="price_details"
                        value={formData.price_details}
                        onChange={(e) => setFormData({ ...formData, price_details: e.target.value })}
                        placeholder="e.g., ₹10,000 or Quote needed"
                        data-testid="enquiry-price-input"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="priority">Priority *</Label>
                        <Select value={formData.priority} onValueChange={(value) => setFormData({ ...formData, priority: value })}>
                          <SelectTrigger data-testid="enquiry-priority-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="low">Low</SelectItem>
                            <SelectItem value="medium">Medium</SelectItem>
                            <SelectItem value="high">High</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="status">Status *</Label>
                        <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                          <SelectTrigger data-testid="enquiry-status-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="new">New</SelectItem>
                            <SelectItem value="contacted">Contacted</SelectItem>
                            <SelectItem value="qualified">Qualified</SelectItem>
                            <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                            <SelectItem value="won">Won</SelectItem>
                            <SelectItem value="lost">Lost</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="follow_up_date">Next Follow-up Date</Label>
                      <Input
                        id="follow_up_date"
                        type="date"
                        value={formData.follow_up_date}
                        onChange={(e) => setFormData({ ...formData, follow_up_date: e.target.value })}
                        data-testid="enquiry-followup-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="remarks">Remarks</Label>
                      <Textarea
                        id="remarks"
                        value={formData.remarks}
                        onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                        rows={2}
                        placeholder="Any additional notes..."
                        data-testid="enquiry-remarks-input"
                      />
                    </div>
                    <div className="pt-4 border-t border-slate-200">
                      <Button type="submit" className="w-full" size="lg" disabled={submitting} data-testid="submit-enquiry-button">
                        {submitting ? 'Saving...' : 'Save Now'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Search Bar and Filters */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by customer name or phone number..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-10"
                  data-testid="enquiry-search-input"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              
              {/* Branch Filter - visible to all users, but admins can see all branches */}
              {branches.length > 0 && (
                <div className="flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-slate-500" />
                  <Select value={branchFilter} onValueChange={setBranchFilter}>
                    <SelectTrigger className="w-40" data-testid="enquiry-branch-filter">
                      <SelectValue placeholder="Filter by Branch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Branches</SelectItem>
                      {branches.map((branch) => (
                        <SelectItem key={branch.id} value={branch.id}>
                          {branch.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-36" data-testid="enquiry-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="contacted">Contacted</SelectItem>
                  <SelectItem value="qualified">Qualified</SelectItem>
                  <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                  <SelectItem value="won">Won</SelectItem>
                  <SelectItem value="lost">Lost</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Assigned To Filter */}
              {filteredTeamMembers.length > 0 && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-500" />
                  <Select value={assignedToFilter} onValueChange={setAssignedToFilter}>
                    <SelectTrigger className="w-40" data-testid="enquiry-assigned-to-filter">
                      <SelectValue placeholder="Assigned To" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Assignees</SelectItem>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {filteredTeamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Created By Filter */}
              {filteredTeamMembers.length > 0 && (
                <div className="flex items-center gap-2">
                  <Select value={createdByFilter} onValueChange={setCreatedByFilter}>
                    <SelectTrigger className="w-40" data-testid="enquiry-created-by-filter">
                      <SelectValue placeholder="Created By" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Creators</SelectItem>
                      {filteredTeamMembers.map((member) => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            {hasActiveFilters && (
              <div className="flex items-center gap-4 mt-2">
                <p className="text-sm text-slate-500">
                  Found {filteredEnquiries.length} enquir{filteredEnquiries.length !== 1 ? 'ies' : 'y'}
                </p>
                <button
                  onClick={clearFilters}
                  className="text-sm text-accent hover:underline"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : filteredEnquiries.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
              <p className="text-slate-600">
                {hasActiveFilters ? 'No enquiries match your search/filter criteria.' : 'No enquiries yet. Create your first enquiry to get started.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredEnquiries.map((enquiry) => (
                <Link
                  key={enquiry.id}
                  to={`/enquiries/${enquiry.id}`}
                  className="bg-white rounded-xl border border-slate-200 p-6 card-hover"
                  data-testid={`enquiry-card-${enquiry.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(enquiry.status)}`}>
                      {enquiry.status.replace('_', ' ')}
                    </span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(enquiry.priority)}`}>
                      {enquiry.priority}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{enquiry.product_name}</h3>
                  <p className="text-sm text-slate-600 mb-4 line-clamp-2">{enquiry.price_details || 'Price not specified'}</p>
                  <div className="text-xs text-slate-500">
                    <p>Customer: {enquiry.customer_name || 'Unknown'}</p>
                    {enquiry.assigned_to_name && <p>Assigned: {enquiry.assigned_to_name}</p>}
                    {enquiry.created_by_name && <p>Created By: {enquiry.created_by_name}</p>}
                    {enquiry.follow_up_date && (
                      <p className="flex items-center gap-1 text-orange-600 font-medium mt-1">
                        <Calendar className="h-3 w-3" />
                        Follow Up: {new Date(enquiry.follow_up_date).toLocaleDateString()}
                      </p>
                    )}
                    <p>Created: {new Date(enquiry.created_at).toLocaleDateString()}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
